import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, request } from '@/lib/api/client'
import { asList } from '@/lib/api/list'

/**
 * Bütçe: yıl başına sürümler (taslak → onaylı; revizyon gerekçeli yeni taslak, onaylanınca önceki sürüm "revize edildi").
 * Satırlar gelir/gider hesabı × ay, isteğe bağlı proje/departman. Kâr/zarar (tahakkuk) onaylı sürümle karşılaştırılır;
 * sapma eşiği aşılınca onay yetkililerine bildirim gider.
 */
export const BUDGET_PERMS = {
  read: 'finance:budget:read',
  edit: 'finance:budget:edit',
  approve: 'finance:budget:approve',
  import: 'finance:budget:import',
} as const

export type BudgetStatus = 'draft' | 'approved' | 'superseded'
export type Budget = {
  id: string
  year: number
  version: number
  status: BudgetStatus
  title: string
  note: string
  revisionReason: string
  basedOn: string
  createdBy: string
  createdAt: string
  approvedBy: string
  approvedAt: string
  expenseTotal: string
}
export type BudgetRow = { glCode: string; glName: string; kind: 'income' | 'expense' | ''; projectId: string; departmentId: string; months: Record<string, string> }
export type BudgetEvent = { userId: string; kind: string; note: string; createdAt: string }
export type BudgetDetail = Budget & { rows: BudgetRow[]; events: BudgetEvent[] }
export type BudgetAccount = { code: string; name: string; kind: 'income' | 'expense' }

type Row = Record<string, unknown>
const s = (v: unknown) => (v == null ? '' : String(v))

const mapBudget = (r: Row): Budget => ({
  id: s(r.id),
  year: Number(r.year) || 0,
  version: Number(r.version) || 0,
  status: (['draft', 'approved', 'superseded'].includes(s(r.status)) ? s(r.status) : 'draft') as BudgetStatus,
  title: s(r.title),
  note: s(r.note),
  revisionReason: s(r.revision_reason),
  basedOn: s(r.based_on),
  createdBy: s(r.created_by),
  createdAt: s(r.created_at),
  approvedBy: s(r.approved_by),
  approvedAt: s(r.approved_at),
  expenseTotal: s(r.expense_total),
})

const mapDetail = (r: Row): BudgetDetail => ({
  ...mapBudget(r),
  rows: asList(r.rows as Row[]).map((x) => ({
    glCode: s(x.gl_code),
    glName: s(x.gl_name),
    kind: x.kind === 'income' ? 'income' : x.kind === 'expense' ? 'expense' : '',
    projectId: s(x.project_id),
    departmentId: s(x.department_id),
    months: (x.months && typeof x.months === 'object' ? x.months : {}) as Record<string, string>,
  })),
  events: asList(r.events as Row[]).map((e) => ({ userId: s(e.user_id), kind: s(e.kind), note: s(e.note), createdAt: s(e.created_at) })),
})

const keys = { all: ['finance', 'budgets'] as const }

export const useBudgets = (year: number) =>
  useQuery({ queryKey: [...keys.all, 'list', year], queryFn: async () => asList(await api.get<Row[]>('/api/v1/finance/budgets', { year: String(year) })).map(mapBudget) })

export const useBudget = (id: string | null) =>
  useQuery({ queryKey: [...keys.all, 'one', id], enabled: !!id, queryFn: async () => mapDetail(await api.get<Row>(`/api/v1/finance/budgets/${id}`)) })

export const useBudgetAccounts = () =>
  useQuery({
    queryKey: [...keys.all, 'accounts'],
    staleTime: 10 * 60_000,
    queryFn: async () =>
      asList(await api.get<Row[]>('/api/v1/finance/budgets/accounts')).map((a): BudgetAccount => ({ code: s(a.code), name: s(a.name), kind: a.kind === 'income' ? 'income' : 'expense' })),
  })

function useBudgetMut<A, R = unknown>(fn: (a: A) => Promise<R>) {
  const qc = useQueryClient()
  return useMutation({ mutationFn: fn, onSuccess: () => qc.invalidateQueries({ queryKey: ['finance'] }) })
}

export type BudgetCreateInput = { year: number; title: string; source: 'empty' | 'actual' | 'budget'; sourceYear: number; pct: string; byProject: boolean; byDepartment: boolean }

export const useCreateBudget = () =>
  useBudgetMut(async (i: BudgetCreateInput) =>
    mapDetail(
      await api.post<Row>('/api/v1/finance/budgets', {
        year: i.year,
        title: i.title.trim(),
        source: i.source,
        source_year: i.sourceYear,
        pct: (i.pct || '0').replace(',', '.'),
        by_project: i.byProject,
        by_department: i.byDepartment,
      }),
    ),
  )

export const useSaveBudget = () =>
  useBudgetMut(async ({ id, title, note, rows }: { id: string; title: string; note: string; rows: BudgetRow[] }) =>
    mapDetail(
      await api.put<Row>(`/api/v1/finance/budgets/${id}`, {
        title,
        note,
        rows: rows
          .filter((r) => r.glCode)
          .map((r) => ({ gl_code: r.glCode, project_id: r.projectId, department_id: r.departmentId, months: Object.fromEntries(Object.entries(r.months).map(([k, v]) => [k, v.replace(',', '.')])) })),
      }),
    ),
  )

export const useApproveBudget = () => useBudgetMut(({ id, note }: { id: string; note: string }) => api.post(`/api/v1/finance/budgets/${id}/approve`, { note }))

export const useReviseBudget = () =>
  useBudgetMut(async ({ id, reason }: { id: string; reason: string }) => mapDetail(await api.post<Row>(`/api/v1/finance/budgets/${id}/revise`, { reason: reason.trim() })))

export const useImportBudget = () =>
  useBudgetMut(async ({ id, file }: { id: string; file: File }) => {
    const form = new FormData()
    form.append('file', file)
    return request('POST', `/api/v1/finance/budgets/${id}/import`, { body: form })
  })

export const downloadBudgetExcel = (b: Budget) => api.download(`/api/v1/finance/budgets/${b.id}/excel`, `Butce_${b.year}_v${b.version}.xlsx`)

export type BudgetSettings = { variancePct: string; notify: boolean }

export const useBudgetSettings = (enabled = true) =>
  useQuery({
    queryKey: [...keys.all, 'settings'],
    enabled,
    queryFn: async (): Promise<BudgetSettings> => {
      const r = await api.get<Row>('/api/v1/finance/budgets/settings')
      return { variancePct: s(r.variance_pct) || '10', notify: r.notify !== false }
    },
  })

export const useSaveBudgetSettings = () =>
  useBudgetMut((i: BudgetSettings) => api.put('/api/v1/finance/budgets/settings', { variance_pct: i.variancePct.replace(',', '.'), notify: i.notify }))

/** Yılın ayları (YYYY-AA). */
export const yearMonths = (year: number) => Array.from({ length: 12 }, (_, i) => `${year}-${String(i + 1).padStart(2, '0')}`)
